// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * File-scan path: step through an uploaded video by seeking, score each frame for
 * motion with the same DSP the live path uses, and capture a snapshot at each
 * motion peak. Decoding and analysis happen in the tab; the file is never
 * uploaded. Seeking (rather than playback) keeps it deterministic and headless.
 */

import { computeLumaGrid, diffGrids, GRID_COLS, GRID_ROWS } from './motion';

const SCAN_W = 160;
const SCAN_H = 120;

export interface ScanSample {
  t: number;
  score: number;
}

export interface ScanOptions {
  stepSec: number;
  cellDelta: number;
  mask?: Uint8Array | null;
  onProgress?: (ratio: number) => void;
  signal?: { aborted: boolean };
}

/** Await enough of a video element to have a duration and a first frame. */
export function whenReady(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve, reject) => {
    if (video.readyState >= 2 && Number.isFinite(video.duration) && video.duration > 0) {
      resolve();
      return;
    }
    const onReady = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        cleanup();
        resolve();
      }
    };
    const onErr = () => {
      cleanup();
      reject(new Error('Could not decode that video'));
    };
    const cleanup = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('durationchange', onReady);
      video.removeEventListener('error', onErr);
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('durationchange', onReady);
    video.addEventListener('error', onErr);
  });
}

/**
 * Seek to `t` and resolve once the decoded frame is available. We wait one
 * presented video frame (requestVideoFrameCallback) for a fresh draw, but ALWAYS
 * race every wait against a timeout: rvfc and 'seeked' are both throttled/paused
 * while the document is hidden, and without the fallback a backgrounded scan would
 * hang forever.
 */
function seek(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.removeEventListener('seeked', onSeeked);
      resolve();
    };
    let afterSeekCalled = false;
    const afterSeek = () => {
      if (afterSeekCalled) return;
      afterSeekCalled = true;
      const rvfc = (video as unknown as { requestVideoFrameCallback?: (cb: () => void) => number })
        .requestVideoFrameCallback;
      if (rvfc) rvfc.call(video, finish);
      setTimeout(finish, 150); // rvfc never fires on a hidden tab
    };
    const onSeeked = () => afterSeek();
    video.addEventListener('seeked', onSeeked);
    try {
      video.currentTime = t;
    } catch {
      finish();
      return;
    }
    setTimeout(afterSeek, 700); // 'seeked' may not fire if time barely changes
  });
}

/** Scan the whole video, returning a motion score per stepped frame. */
export async function scanVideo(video: HTMLVideoElement, opts: ScanOptions): Promise<{ samples: ScanSample[]; duration: number }> {
  await whenReady(video);
  const duration = video.duration;
  const scratch = document.createElement('canvas');
  scratch.width = SCAN_W;
  scratch.height = SCAN_H;
  const ctx = scratch.getContext('2d', { willReadFrequently: true })!;
  const samples: ScanSample[] = [];
  let prev: Float32Array | null = null;
  const end = Math.max(0, duration - 0.02);

  for (let t = 0; t <= end + 1e-6; t += opts.stepSec) {
    if (opts.signal?.aborted) break;
    const ct = Math.min(t, end);
    await seek(video, ct);
    ctx.drawImage(video, 0, 0, SCAN_W, SCAN_H);
    const grid = computeLumaGrid(ctx.getImageData(0, 0, SCAN_W, SCAN_H).data, SCAN_W, SCAN_H, GRID_COLS, GRID_ROWS);
    const score = prev ? diffGrids(prev, grid, opts.cellDelta, opts.mask ?? null).score : 0;
    samples.push({ t: ct, score });
    prev = grid;
    opts.onProgress?.(duration ? ct / duration : 1);
  }
  opts.onProgress?.(1);
  return { samples, duration };
}

/** Grab a JPEG snapshot of the video at time `t`. */
export async function captureFrameAt(video: HTMLVideoElement, t: number, maxW = 640): Promise<Blob> {
  await seek(video, t);
  const vw = video.videoWidth || SCAN_W;
  const vh = video.videoHeight || SCAN_H;
  const scale = Math.min(1, maxW / vw);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(video, 0, 0, w, h);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('snapshot failed'))), 'image/jpeg', 0.82);
  });
}
